/**
 * Camera cutaway for enclosed dungeons (docs/design/dungeon-layout-study.md §6), Diablo / Lost Ark
 * style, so walled and vaulted rooms stay readable from the high default camera:
 *
 *  - walls (kind 1): above their cut height (`aCut.z`, ~1.8 yd over the wall foot) a wall is cut
 *    away when it faces the camera (its outward normal points toward the camera: the near walls
 *    of every room on screen) while the camera is high, and anything standing in the wedge
 *    between the party and the camera is cut at any zoom. Below the cut height walls are solid
 *    (the stub reads as the room's outline; a cap at the cut height closes the section).
 *  - overheads (kind 2: beams, architraves, arcade roofs): cut inside the wedge, and around the
 *    party while the camera is high.
 *  - ceilings (kind 3): drawn only while the camera is below their spring line (`aCut.z`), i.e.
 *    close zoom inside the room; from above they vanish.
 *
 * Cuts are a screen-door dither (opaque pipeline, no sorting, no shadow changes). The decision
 * math lives in GLSL; `cutParams` (pure, unit-tested) turns the camera into the uniforms.
 * Per-vertex `aCut` = (outward normal x, outward normal z in three space, cut height, kind).
 */
import * as THREE from 'three';
import { ownedToonMaterial } from './style';

export const CUT_KIND = { never: 0, wall: 1, overhead: 2, ceiling: 3 } as const;

/** Shared uniforms (one set per page: one dungeon at a time). */
export const CUT_U = {
  uCutCam: { value: new THREE.Vector3(0, 1e4, 0) },
  uCutFocus: { value: new THREE.Vector3() },
  /** x: near-wall radius, y: wedge half-width at the party, z: high-camera amount (0..1), w: wedge widening per yd. */
  uCutP: { value: new THREE.Vector4(46, 2.6, 1, 0.35) },
  /** x: overhead clear radius around the party (high camera). */
  uCutQ: { value: new THREE.Vector4(9, 0, 0, 0) },
};

const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

export interface CutParams {
  /** How much the high-camera rules apply (0 close / low, 1 default view). */
  high: number;
  /** Wedge half-width at the party and its growth per yard toward the camera. */
  wedge0: number;
  wedgeSlope: number;
  nearRadius: number;
  overheadRadius: number;
}

/** Cutaway strength from the camera's height above the party's floor (yards). Pure. */
export function cutParams(camHeightAboveFocus: number): CutParams {
  const high = smooth(5, 11, camHeightAboveFocus);
  return {
    high,
    wedge0: 1.6 + 1.2 * high,
    wedgeSlope: 0.15 + 0.25 * high,
    nearRadius: 46,
    overheadRadius: 9 * high,
  };
}

/** Point the cutaway at this frame's camera and party focus (three.js space). */
export function updateCutaway(cam: THREE.Vector3, focusX: number, focusY: number, focusZ: number): void {
  const p = cutParams(cam.y - focusY);
  CUT_U.uCutCam.value.copy(cam);
  CUT_U.uCutFocus.value.set(focusX, focusY + 1.1, focusZ);
  CUT_U.uCutP.value.set(p.nearRadius, p.wedge0, p.high, p.wedgeSlope);
  CUT_U.uCutQ.value.x = p.overheadRadius;
}

/** Park the cutaway (nothing cut, ceilings hidden) — e.g. before the first frame. */
export function resetCutaway(): void {
  CUT_U.uCutCam.value.set(0, 1e4, 0);
  CUT_U.uCutP.value.z = 0;
}

const VERT_DECL = `attribute vec4 aCut;\nvarying vec4 vCut;\nvarying vec3 vCutW;`;
const VERT_BODY = `
  {
    vec4 cw = vec4(transformed, 1.0);
    #ifdef USE_INSTANCING
      cw = instanceMatrix * cw;
    #endif
    vCutW = (modelMatrix * cw).xyz;
    vCut = aCut;
  }`;
const FRAG_DECL = `
  varying vec4 vCut;
  varying vec3 vCutW;
  uniform vec3 uCutCam;
  uniform vec3 uCutFocus;
  uniform vec4 uCutP;
  uniform vec4 uCutQ;
  float cutBayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
  float cutBayer4(vec2 a) { return cutBayer2(0.5 * a) * 0.25 + cutBayer2(a); }`;
const FRAG_BODY = `
  {
    float cutK = 0.0;
    float kind = vCut.w;
    if (kind > 2.5) {
      // Ceiling: only while the camera is below its spring line.
      cutK = smoothstep(vCut.z - 1.0, vCut.z + 0.6, uCutCam.y);
    } else if (kind > 0.5 && vCutW.y > vCut.z) {
      vec2 dv = uCutCam.xz - uCutFocus.xz;
      float L = length(dv);
      vec2 u = L > 0.01 ? dv / L : vec2(0.0, 1.0);
      vec2 rel = vCutW.xz - uCutFocus.xz;
      float along = dot(rel, u);
      float perp = abs(rel.x * u.y - rel.y * u.x);
      float wb = uCutP.y + max(along, 0.0) * uCutP.w;
      float wedge = (1.0 - smoothstep(wb - 1.0, wb, perp)) * smoothstep(-1.5, 0.0, along) * (1.0 - smoothstep(L + 2.0, L + 6.0, along));
      float dF = length(rel);
      float a = 0.0;
      if (kind < 1.5) {
        // Near walls (outward normal toward the camera) on screen, while the camera is high.
        float facing = dot(vCut.xy, u);
        a = smoothstep(0.2, 0.5, facing) * (1.0 - smoothstep(uCutP.x * 0.85, uCutP.x, dF)) * uCutP.z;
      } else {
        a = (1.0 - smoothstep(uCutQ.x * 0.7, uCutQ.x, dF)) * uCutP.z;
      }
      cutK = max(a, wedge);
    }
    if (cutK > 0.002 && cutK * 1.02 > cutBayer4(gl_FragCoord.xy) + 0.01) discard;
  }`;

/**
 * A toon material whose geometry carries `aCut` and obeys the cutaway. Same look as
 * ownedToonMaterial(params); `key` separates the program cache from the plain variant.
 */
export function cutawayMaterial(params: THREE.MeshToonMaterialParameters): THREE.MeshToonMaterial {
  const m = ownedToonMaterial(params);
  const base = m.onBeforeCompile;
  m.onBeforeCompile = (sh, r) => {
    base.call(m, sh, r);
    Object.assign(sh.uniforms, CUT_U);
    sh.vertexShader = sh.vertexShader.replace('#include <common>', `#include <common>\n${VERT_DECL}`).replace('#include <project_vertex>', `#include <project_vertex>\n${VERT_BODY}`);
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', `#include <common>\n${FRAG_DECL}`).replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>\n${FRAG_BODY}`);
  };
  m.customProgramCacheKey = () => `toon-cut|${m.map ? 'map' : 'nomap'}`;
  return m;
}
